import React, { Suspense, lazy, useState, useEffect, useCallback } from 'react';
import { supabase } from './services/external/supabase';
import { getUserProjects } from './services/project/projectService';

const MainLandingPage = lazy(() => import('./pages/MainLandingPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SavedVideosPage = lazy(() => import('./pages/SavedVideosPage'));
const TemplatesCatalogPage = lazy(() => import('./pages/TemplatesCatalogPage/TemplatesCatalogPage'));
const TemplateEditorPage = lazy(() => import('./pages/TemplateEditorPage/TemplateEditorPage'));
const InfoPage = lazy(() => import('./pages/InfoPage'));
const DEFAULT_TEMPLATE_ID = 'text-scene';
const CATALOG_TAB_OPTIONS = new Set(['all', 'quick-shots', 'social-content', 'misc']);
const PROTECTED_PAGES = new Set(['saved-videos']);
const AUTH_DISABLED = ['1', 'true', 'yes', 'on'].includes(String(import.meta.env.VITE_DISABLE_AUTH || '').trim().toLowerCase());
const DEV_BYPASS_USER = {
  id: 'dev-local-user',
  name: 'Developer',
  email: 'dev@local.test',
  avatar: '👨‍💻',
};

const SHORT_VIDEO_TEMPLATE_IDS = new Set([
  'text-scene',
  'image-scene',
  'web-search',
  'message-notification',
  'social-handle',
  'calendar-card',
  'social-media-post',
  'common-scenes',
  'paper-focus-scene',
  'paper-kinetic-words',
  'paper-highlight-path',
  'newspaper-match-cut',
  'web-match-cut',
]);

const PAGE_TO_PATH = {
  home: '/',
  login: '/login',
  features: '/features',
  'templates-catalog': '/templates',
  'template-editor': '/editor',
  'saved-videos': '/saved-videos',
  'about-us': '/about-us',
  'contact-us': '/contact-us',
  'privacy-policy': '/privacy-policy',
  'terms-conditions': '/terms-conditions',
  'acceptable-use-policy': '/acceptable-use-policy',
  faq: '/faq',
};

const PATH_TO_PAGE = Object.entries(PAGE_TO_PATH).reduce((acc, [page, path]) => {
  acc[path] = page;
  return acc;
}, {});
PATH_TO_PAGE['/home'] = 'home';

const INITIAL_AUTO_REDIRECT_KEY = 'didInitialAutoRedirect';

function normalizeTemplateId(value) {
  return String(value || '').trim().toLowerCase();
}

function normalizeCatalogTab(value) {
  const safe = String(value || '').trim().toLowerCase();
  return CATALOG_TAB_OPTIONS.has(safe) ? safe : 'all';
}

function readCatalogTabFromSearch(search) {
  const params = new URLSearchParams(String(search || ''));
  if (!params.has('catalogTab')) return null;
  return normalizeCatalogTab(params.get('catalogTab'));
}

function toUserData(sessionUser) {
  if (!sessionUser) return null;
  return {
    id: sessionUser.id,
    name: sessionUser.user_metadata?.name || sessionUser.email.split('@')[0],
    email: sessionUser.email,
    avatar: '👨‍💻',
  };
}

function getRouteStateFromPath(pathname) {
  const rawPath = String(pathname || '').trim();
  if (!rawPath || rawPath === '/') {
    return { page: 'home', templateId: null };
  }

  if (rawPath.startsWith('/editor/')) {
    const templateId = normalizeTemplateId(rawPath.slice('/editor/'.length).split('/')[0]);
    if (SHORT_VIDEO_TEMPLATE_IDS.has(templateId)) {
      return { page: 'template-editor', templateId };
    }
    return { page: 'template-editor', templateId: DEFAULT_TEMPLATE_ID };
  }
  if (SHORT_VIDEO_TEMPLATE_IDS.has(rawPath.slice(1))) {
    return { page: 'template-editor', templateId: rawPath.slice(1) };
  }

  return {
    page: PATH_TO_PAGE[rawPath] || 'home',
    templateId: null,
  };
}

function GlobalPageLoader({ message = 'جاري تحميل الصفحة...' }) {
  return (
    <>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10000,
          background: 'rgba(3, 8, 18, 0.86)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
            color: '#e2e8f0',
            fontSize: '18px',
            fontWeight: '600',
          }}
        >
          <div style={{ display: 'inline-flex', gap: '8px' }} aria-hidden="true">
            <span className="global-loader-dot"></span>
            <span className="global-loader-dot"></span>
            <span className="global-loader-dot"></span>
          </div>
          <div>{message}</div>
        </div>
      </div>
      <style>{`
        .global-loader-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: var(--primary, #22c55e);
          animation: globalLoaderPulse 1s infinite ease-in-out;
        }

        .global-loader-dot:nth-child(2) {
          animation-delay: 0.15s;
        }

        .global-loader-dot:nth-child(3) {
          animation-delay: 0.3s;
        }

        @keyframes globalLoaderPulse {
          0%, 80%, 100% {
            transform: scale(0.7);
            opacity: 0.45;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
}

function App() {
  const initialRouteState = getRouteStateFromPath(window.location.pathname);
  const initialCatalogTab = readCatalogTabFromSearch(window.location.search)
    || normalizeCatalogTab(window.sessionStorage.getItem('lastCatalogTab'))
    || 'all';
  const [currentPage, setCurrentPage] = useState(() => initialRouteState.page);
  const [activeTemplateId, setActiveTemplateId] = useState(() => initialRouteState.templateId);
  const [lastCatalogTab, setLastCatalogTab] = useState(() => initialCatalogTab);
  const [routeLoading, setRouteLoading] = useState(true);

  const [user, setUser] = useState(() => (AUTH_DISABLED ? DEV_BYPASS_USER : null));

  const [loading, setLoading] = useState(() => !AUTH_DISABLED);
  const [showAuthAlert, setShowAuthAlert] = useState(false);

  const hasSavedVideosForUser = useCallback(async (userId) => {
    const projects = await getUserProjects(userId);
    return (projects || []).some((project) => project?.video_path || project?.video_url);
  }, []);

  const scrollToHashTarget = useCallback((hashValue) => {
    const raw = String(hashValue || '').replace(/^#/, '').trim();
    if (!raw) return;
    const target = document.getElementById(raw);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const navigateToPage = useCallback((page, { replace = false, query = null, hash = null, templateId = null } = {}) => {
    const requestedPage = PAGE_TO_PATH[page] ? page : 'home';
    let safePage = requestedPage;
    let resolvedTemplateId = normalizeTemplateId(templateId);

    if (SHORT_VIDEO_TEMPLATE_IDS.has(safePage)) {
      resolvedTemplateId = safePage;
      safePage = 'template-editor';
    }

    if (safePage === 'template-editor') {
      if (!SHORT_VIDEO_TEMPLATE_IDS.has(resolvedTemplateId)) {
        resolvedTemplateId = SHORT_VIDEO_TEMPLATE_IDS.has(activeTemplateId)
          ? activeTemplateId
          : DEFAULT_TEMPLATE_ID;
      }
    } else {
      resolvedTemplateId = null;
    }

    const nextPath = safePage === 'template-editor'
      ? `/editor/${resolvedTemplateId}`
      : PAGE_TO_PATH[safePage];
    const normalizedQuery = { ...(query || {}) };
    if (safePage === 'templates-catalog') {
      normalizedQuery.catalogTab = normalizeCatalogTab(normalizedQuery.catalogTab || lastCatalogTab);
    }
    const searchParams = new URLSearchParams(normalizedQuery);
    const searchPart = searchParams.toString() ? `?${searchParams.toString()}` : '';
    const hashPart = hash ? `#${String(hash).replace(/^#/, '')}` : '';
    const nextUrl = `${nextPath}${searchPart}${hashPart}`;

    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const samePageNavigation = currentPage === safePage;
    if (currentUrl !== nextUrl || currentPage !== safePage) {
      setRouteLoading(!samePageNavigation);
      setCurrentPage(safePage);
      setActiveTemplateId(resolvedTemplateId);
      const shouldReplaceHistory = replace || (
        currentPage === 'template-editor' && safePage === 'template-editor'
      );
      const method = shouldReplaceHistory ? 'replaceState' : 'pushState';
      window.history[method]({}, '', nextUrl);
      if (samePageNavigation && hashPart) {
        window.requestAnimationFrame(() => {
          scrollToHashTarget(hashPart);
        });
      }
    } else {
      setRouteLoading(false);
    }
  }, [activeTemplateId, currentPage, lastCatalogTab, scrollToHashTarget]);

  useEffect(() => {
    if (loading) return;
    const expectedPath = currentPage === 'template-editor'
      ? `/editor/${SHORT_VIDEO_TEMPLATE_IDS.has(activeTemplateId) ? activeTemplateId : DEFAULT_TEMPLATE_ID}`
      : (PAGE_TO_PATH[currentPage] || PAGE_TO_PATH.home);
    if (window.location.pathname !== expectedPath) {
      window.history.replaceState({}, '', expectedPath);
    }
  }, [activeTemplateId, currentPage, loading]);

  const handlePageReady = useCallback(() => {
    setRouteLoading(false);
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      setRouteLoading(true);
      const routeState = getRouteStateFromPath(window.location.pathname);
      setCurrentPage(routeState.page);
      setActiveTemplateId(routeState.templateId);
      if (routeState.page === 'templates-catalog') {
        const tabFromUrl = readCatalogTabFromSearch(window.location.search);
        if (tabFromUrl) setLastCatalogTab(tabFromUrl);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    try {
      window.sessionStorage.setItem('lastCatalogTab', normalizeCatalogTab(lastCatalogTab));
    } catch (_) {
      // ignore storage failures
    }
  }, [lastCatalogTab]);

  useEffect(() => {
    if (AUTH_DISABLED) return undefined;

    const checkSession = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (session?.user) {
          const userData = toUserData(session.user);
          setUser(userData);

          // Run auto-redirect once per session, and only when landing on root "/".
          const isRootEntry = window.location.pathname === '/';
          const didInitialAutoRedirect = window.sessionStorage.getItem(INITIAL_AUTO_REDIRECT_KEY) === 'true';

          if (isRootEntry && !didInitialAutoRedirect) {
            try {
              await hasSavedVideosForUser(userData.id);
              const nextPage = 'home';
              const nextPath = PAGE_TO_PATH[nextPage];

              setCurrentPage(nextPage);
              if (window.location.pathname !== nextPath) {
                window.history.replaceState({}, '', nextPath);
              }
              window.sessionStorage.setItem(INITIAL_AUTO_REDIRECT_KEY, 'true');
            } catch (projectsError) {
              console.error('? Failed to check saved videos on app load:', projectsError);
              setCurrentPage('home');
              if (window.location.pathname !== PAGE_TO_PATH.home) {
                window.history.replaceState({}, '', PAGE_TO_PATH.home);
              }
              window.sessionStorage.setItem(INITIAL_AUTO_REDIRECT_KEY, 'true');
            }
          }
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('? Session check error:', error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        setUser(null);
      } else if (event === 'SIGNED_IN' && session?.user) {
        const userData = toUserData(session.user);
        setUser(userData);
      }
    });

    return () => subscription.unsubscribe();
  }, [hasSavedVideosForUser]);

  useEffect(() => {
    if (loading || AUTH_DISABLED) return;

    if (PROTECTED_PAGES.has(currentPage) && !user) {
      navigateToPage('login', { replace: true });
    }
  }, [loading, currentPage, user, navigateToPage]);

  const handleNavigation = (page, options = {}) => {
    if (page === 'saved-videos') {
      navigateToPage('templates-catalog', { replace: true });
      return;
    }
    if (!AUTH_DISABLED && PROTECTED_PAGES.has(page) && !user) {
      setShowAuthAlert(true);
      setTimeout(() => setShowAuthAlert(false), 3000);
      navigateToPage('login');
      return;
    }

    navigateToPage(page, options);
  };

  const handleCatalogTabChange = useCallback((nextTab) => {
    const normalizedTab = normalizeCatalogTab(nextTab);
    setLastCatalogTab(normalizedTab);

    if (currentPage !== 'templates-catalog') return;

    const params = new URLSearchParams(window.location.search);
    if (params.get('catalogTab') === normalizedTab) return;
    params.set('catalogTab', normalizedTab);
    const nextSearch = params.toString() ? `?${params.toString()}` : '';
    const nextUrl = `${window.location.pathname}${nextSearch}${window.location.hash || ''}`;
    window.history.replaceState({}, '', nextUrl);
  }, [currentPage]);

  const handleLogin = async (userData) => {
    setUser(userData);

    navigateToPage('templates-catalog');
  };

  const handleLogout = async () => {
    if (AUTH_DISABLED) {
      navigateToPage('home', { replace: true });
      return;
    }

    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('? Logout error:', error);
      }
      setUser(null);
      navigateToPage('login', { replace: true });
    } catch (error) {
      console.error('? Logout error:', error);
    }
  };

  if (loading) {
    return <GlobalPageLoader message="جاري تجهيز التطبيق..." />;
  }

  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return <MainLandingPage onNavigate={handleNavigation} user={user} onLogout={handleLogout} onPageReady={handlePageReady} />;
      case 'login':
        if (AUTH_DISABLED) {
          return <MainLandingPage onNavigate={handleNavigation} user={user} onLogout={handleLogout} onPageReady={handlePageReady} />;
        }
        return <LoginPage onNavigate={handleNavigation} onLogin={handleLogin} onPageReady={handlePageReady} />;
      case 'templates-catalog':
        return (
          <TemplatesCatalogPage
            onNavigate={handleNavigation}
            user={user}
            onLogout={handleLogout}
            onPageReady={handlePageReady}
            initialActiveTab={lastCatalogTab}
            onActiveTabChange={handleCatalogTabChange}
          />
        );
      case 'saved-videos':
        return (
          <TemplatesCatalogPage
            onNavigate={handleNavigation}
            user={user}
            onLogout={handleLogout}
            onPageReady={handlePageReady}
            initialActiveTab={lastCatalogTab}
            onActiveTabChange={handleCatalogTabChange}
          />
        );
      case 'template-editor':
        return (
            <TemplateEditorPage
            templateId={activeTemplateId || DEFAULT_TEMPLATE_ID}
            onPageReady={handlePageReady}
            onNavigate={handleNavigation}
            user={user}
            onLogout={handleLogout}
          />
        );
      case 'about-us':
      case 'features':
      case 'contact-us':
      case 'privacy-policy':
      case 'terms-conditions':
      case 'acceptable-use-policy':
      case 'faq':
        return (
          <InfoPage
            pageKey={currentPage}
            onNavigate={handleNavigation}
            onPageReady={handlePageReady}
          />
        );
      default:
        return <MainLandingPage onNavigate={handleNavigation} user={user} onLogout={handleLogout} onPageReady={handlePageReady} />;
    }
  };

  return (
    <>
      <Suspense fallback={<GlobalPageLoader message="جاري تحميل الصفحة..." />}>
        {renderPage()}
      </Suspense>

      {routeLoading && (
        <GlobalPageLoader message="جاري تحميل الصفحة..." />
      )}

      {showAuthAlert && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
            color: 'white',
            padding: '1rem 1.5rem',
            borderRadius: '10px',
            boxShadow: '0 4px 15px rgba(34, 197, 94, 0.3)',
            zIndex: 9999,
            fontSize: '0.9rem',
            fontWeight: '500',
            animation: 'slideInRight 0.3s ease-out',
            maxWidth: '300px',
            textAlign: 'center',
          }}
        >
          🔒 يجب تسجيل الدخول أولًا للوصول إلى هذه الصفحة
        </div>
      )}

      <style>{`
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(100px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </>
  );
}

export default App;





